import React, { useState, useEffect } from 'react';
import { 
  FaWindows, FaAndroid, FaDownload, FaChevronDown, 
  FaChevronUp, FaFlask, FaExclamationTriangle, FaSpinner,
  FaGooglePlay, FaHistory
} from 'react-icons/fa';
import SEO from '../components/SEO';
import useDownloadsContent from '../hooks/useDownloadsContent';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async'; // Thêm dòng import này

const Downloads = () => {
  const [activeTab, setActiveTab] = useState('windows');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const { changelogs, faq, loading, error } = useDownloadsContent();
  const [selectedVersion, setSelectedVersion] = useState({
    windows: null,
    android: null
  });

  useEffect(() => {
    if (!loading && !error) {
      setSelectedVersion({
        windows: changelogs.windows.length > 0 ? changelogs.windows[0].version : null,
        android: changelogs.android.length > 0 ? changelogs.android[0].version : null
      });
    }
  }, [loading, error, changelogs]);

  const requirements = {
    windows: [
      { label: "OS", value: "Windows 10 (64bit)" },
      { label: "Processor", value: "Intel Core i5-6600k/AMD FX-6300" },
      { label: "RAM", value: "6GB" },
      { label: "Graphics Card", value: "From 2GB Video Memory" },
      { label: "Disk Space", value: "12 GB" }
    ],
    android: [
      { label: "OS", value: "Android 5 and above" },
      { label: "Processor", value: "Frequency from 1.2Ghz" },
      { label: "RAM", value: "4GB" },
      { label: "Disk Space", value: "8GB" }
    ]
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const findSelectedChangelog = (platform) => {
    const version = selectedVersion[platform];
    return changelogs[platform].find(log => log.version === version) || null;
  };

  const handleVersionChange = (e, platform) => {
    setSelectedVersion(prev => ({
      ...prev,
      [platform]: e.target.value
    }));
  };

  return (
    <>
      <SEO 
        title="Tải PSA Roleplay trên PC & Mobile"
        description="Tải launcher và cài đặt PSA Online Launcher để bắt đầu hành trình của bạn trong thế giới PSA Roleplay."
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Tải PSA Roleplay trên PC & Mobile",
            "description": "Tải PSA Online Launcher cho Windows và Android. Tải xuống phiên bản mới nhất để trải nghiệm máy chủ PSA Online tốt nhất.",
            "operatingSystem": "Windows / Android",
            "applicationCategory": "Game",
            "downloadUrl": "https://psa.world/download"
          })}
        </script>

        <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Trang chủ",
                "item": "https://psa.world"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Tải PSA Roleplay trên PC & Mobile",
                "item": "https://psa.world/download"
              }
            ]
          })}
      </script>
      </Helmet>
      
      <div className="min-h-screen pt-24 pb-16 bg-dark-purple-main relative overflow-hidden">
        <div className="page-download-bg absolute inset-0 opacity-30"></div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-purple to-dark-purple-main"></div>
        
        <div className="container mx-auto px-4 relative z-10">
    
          <motion.div 
            className="container mx-auto px-4 relative z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className="text-center mb-12 mt-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-4">
                <span className="text-white neon-glow-white">TẢI VỀ</span>
              </h1>
              
              <motion.div 
                className="w-24 h-1 bg-accent-teal mx-auto mb-6 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: 96 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
              
              <p className="text-light/80 text-lg max-w-2xl mx-auto">
                Tải xuống phiên bản launcher mới nhất của chúng tôi để bắt đầu trải nghiệm PSA Online tốt nhất. Chúng tôi hỗ trợ cả Windows và Android.
              </p>
            </motion.div>
          
            <div className="relative mb-16">
              <div className="launcher-card rounded-3xl bg-gradient-to-br from-dark/80 to-dark/40 backdrop-blur-md border border-white/10 shadow-xl w-full h-[602px] mx-auto overflow-hidden z-10 relative">
                <div className="flex flex-col h-full p-8 md:p-12">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6 text-white">System Requirements</h3>
                  
                  <div className="inline-flex bg-dark/30 rounded-full p-1 self-start mb-8 relative z-50 pointer-events-auto">
                    <button 
                      className={`px-6 py-2 rounded-full text-md font-medium transition-all platform-tab ${
                        activeTab === 'windows' 
                          ? 'bg-white text-dark platform-tab-active' 
                          : 'text-light hover:bg-dark/20'
                      }`}
                      onClick={() => setActiveTab('windows')}
                    >
                      <span className="flex items-center">
                        <FaWindows className="mr-2" />
                        Windows
                      </span>
                    </button>
                    
                    <button 
                      className={`px-6 py-2 rounded-full text-md font-medium transition-all platform-tab ${
                        activeTab === 'android' 
                          ? 'bg-white text-dark platform-tab-active' 
                          : 'text-light hover:bg-dark/20'
                      }`}
                      onClick={() => setActiveTab('android')}
                    >
                      <span className="flex items-center">
                        <FaAndroid className="mr-2" />
                        Android
                      </span>
                    </button>
                  </div>
                  
                  <div className="w-full md:w-1/2 relative h-full z-20">
                    <div className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'windows' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                      <div className="space-y-4 mb-8">
                        {requirements.windows.map((req, index) => (
                          <div key={index} className="flex">
                            <div className="w-1/3 text-light/70 text-lg">{req.label}</div>
                            <div className="w-2/3 text-white font-medium text-lg">{req.value}</div>
                          </div>
                        ))}
                      </div>
                      
                      <a 
                        href="/downloads/psa-launcher-windows.exe" 
                        className="inline-flex items-center bg-accent-teal hover:bg-accent-teal/90 text-dark font-medium px-8 py-3 rounded-lg transition-all text-lg"
                      >
                        <FaDownload className="mr-2" />
                        Download Game
                      </a>
                    </div>
                    
                    <div className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'android' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                      <div className="space-y-4 mb-8">
                        {requirements.android.map((req, index) => (
                          <div key={index} className="flex">
                            <div className="w-1/3 text-light/70 text-lg">{req.label}</div>
                            <div className="w-2/3 text-white font-medium text-lg">{req.value}</div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex flex-wrap gap-3">
                        <a 
                          href="https://play.google.com/store/apps/details?id=com.psa.launcher" 
                          className="inline-flex items-center bg-[#00C853] hover:bg-[#00C853]/90 text-white font-medium px-5 py-3 rounded-lg transition-all"
                        >
                          <FaGooglePlay className="mr-2" />
                          Play Market
                        </a>
                        
                        <a 
                          href="/downloads/psa-launcher-android.apk" 
                          className="inline-flex items-center bg-accent-teal hover:bg-accent-teal/90 text-dark font-medium px-5 py-3 rounded-lg transition-all"
                        >
                          <FaDownload className="mr-2" />
                          Direct Download
                        </a>
                        
                        <a 
                          href="/downloads/psa-launcher-android-test.apk" 
                          className="inline-flex items-center bg-amber-500 hover:bg-amber-500/90 text-dark font-medium px-5 py-3 rounded-lg transition-all"
                        >
                          <FaFlask className="mr-2" />
                          Test Version
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-0 right-0 z-20 h-full hidden md:block pointer-events-none" style={{ width: '50%' }}>
                <div 
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform pointer-events-none ${
                    activeTab === 'windows' 
                      ? 'translate-x-0 opacity-100' 
                      : 'translate-x-full opacity-0'
                  }`}
                >
                  <img 
                    src="https://psa.world/resources/images/psa_pc.png" 
                    alt="Windows Launcher" 
                    className="max-h-[700px] max-w-full object-contain pointer-events-none"
                    style={{ transform: 'scale(1.25)' }}
                  />
                </div>
                
                <div 
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform pointer-events-none ${
                    activeTab === 'android' 
                      ? 'translate-x-0 opacity-100' 
                      : '-translate-x-full opacity-0'
                  }`}
                >
                  <img 
                    src="https://psa.world/resources/images/psa_android.png" 
                    alt="Android Gameplay" 
                    className="max-h-[700px] max-w-full object-contain pointer-events-none"
                    style={{ transform: 'scale(1.05)' }}
                  />
                </div>
              </div>
              
              <div className="mt-6 block md:hidden">
                <div className="relative h-[300px]">
                  <div 
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform ${
                      activeTab === 'windows' 
                        ? 'translate-x-0 opacity-100' 
                        : 'translate-x-full opacity-0'
                    }`}
                  >
                    <img 
                      src="https://psa.world/resources/images/psa_pc.png" 
                      alt="Windows Launcher" 
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  
                  <div 
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform ${
                      activeTab === 'android' 
                        ? 'translate-x-0 opacity-100' 
                        : '-translate-x-full opacity-0'
                    }`}
                  >
                    <img 
                      src="https://psa.world/resources/images/psa_android.png" 
                      alt="Android Gameplay" 
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Changelog</h2>
              
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <FaSpinner className="animate-spin text-accent-teal text-3xl" />
                </div>
              ) : error ? (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 text-center">
                  <FaExclamationTriangle className="text-red-500 text-2xl mb-2 mx-auto" />
                  <p className="text-white">Không thể tải dữ liệu changelog. Vui lòng thử lại sau.</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Platform Tabs for Changelogs */}
                  <div className="inline-flex bg-dark/30 rounded-full p-1 mb-6">
                    <button 
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all changelog-platform-tab ${
                        activeTab === 'windows' 
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                          : 'text-light/80 hover:text-light hover:bg-dark/40'
                      }`}
                      onClick={() => setActiveTab('windows')}
                    >
                      <span className="flex items-center">
                        <FaWindows className="mr-2" />
                        Windows
                      </span>
                    </button>
                    
                    <button 
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all changelog-platform-tab ${
                        activeTab === 'android' 
                          ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' 
                          : 'text-light/80 hover:text-light hover:bg-dark/40'
                      }`}
                      onClick={() => setActiveTab('android')}
                    >
                      <span className="flex items-center">
                        <FaAndroid className="mr-2" />
                        Android
                      </span>
                    </button>
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Version Selector */}
                      <div className="flex items-center mb-6">
                        <div className="flex items-center mr-2 text-light/80">
                          <FaHistory className="mr-2" />
                          <span>Phiên bản:</span>
                        </div>
                        <div className="relative">
                          <select
                            value={selectedVersion[activeTab] || ''}
                            onChange={(e) => handleVersionChange(e, activeTab)}
                            className="changelog-version-select"
                            style={{ minWidth: '180px' }}
                          >
                            {changelogs[activeTab].map((log) => (
                              <option key={log.version} value={log.version}>
                                {log.version} ({log.date})
                              </option>
                            ))}
                          </select>
                          <FaChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-accent-teal pointer-events-none" size={12} />
                        </div>
                      </div>

                      {/* Selected Changelog Display */}
                      {findSelectedChangelog(activeTab) && (
                        <div className="bg-gradient-to-br from-dark/80 to-dark/40 backdrop-blur-md border border-white/10 rounded-xl p-6">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-accent-teal">Version {findSelectedChangelog(activeTab).version}</h3>
                            <span className="text-light/60 text-sm">{findSelectedChangelog(activeTab).date}</span>
                          </div>
                          
                          <ul className="space-y-2">
                            {findSelectedChangelog(activeTab).changes.map((change, idx) => (
                              <li key={idx} className="text-light/80 flex items-start">
                                <span className="text-accent-teal mr-2 text-lg">•</span>
                                {change}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </section>
            
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Câu Hỏi Thường Gặp</h2>
              
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <FaSpinner className="animate-spin text-accent-teal text-3xl" />
                </div>
              ) : error ? (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 text-center">
                  <FaExclamationTriangle className="text-red-500 text-2xl mb-2 mx-auto" />
                  <p className="text-white">Không thể tải dữ liệu FAQ. Vui lòng thử lại sau.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {faq.map((item, index) => (
                    <div 
                      key={index} 
                      className="bg-gradient-to-br from-dark/80 to-dark/40 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden"
                    >
                      <button
                        className="w-full px-6 py-4 text-left flex justify-between items-center"
                        onClick={() => toggleFaq(index)}
                      >
                        <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                        {openFaqIndex === index ? 
                          <FaChevronUp className="text-accent-teal" /> : 
                          <FaChevronDown className="text-accent-teal" />
                        }
                      </button>
                      
                      <AnimatePresence>
                        {openFaqIndex === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-4 border-t border-white/10 pt-4">
                              <p className="text-light/80">{item.answer}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Downloads;
